import { XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { uploadSummary, type StagedAttachment } from './draftUploads';
import type { TaskDraft } from './taskDraftStore';

/**
 * One minimized draft in the dock (spec 121 FR-002): pressing it expands the
 * dialog where the person is; the × beside it discards, with Undo.
 */
export function DraftChip({
  draft,
  boardName,
  items,
  hidden,
  onOpen,
  onDiscard,
}: {
  draft: TaskDraft;
  /** `null` when the board no longer exists (FR-014); `undefined` while unknown. */
  boardName: string | null | undefined;
  items: readonly StagedAttachment[];
  /**
   * The draft is the open dialog. Hidden rather than unmounted, so focus can
   * come back to this same chip when the dialog folds again.
   */
  hidden: boolean;
  onOpen: () => void;
  onDiscard: () => void;
}) {
  const title = draft.title.trim() || 'Untitled';
  const board =
    boardName === null ? 'Board no longer exists' : (boardName ?? null);
  const uploading = draft.createdTaskId !== null ? uploadSummary(items) : null;
  const text = ['New task', title, board, uploading]
    .filter((part) => part !== null)
    .join(' · ');
  return (
    <div
      hidden={hidden}
      className="bg-popover text-popover-foreground border-border shadow-floating pointer-events-auto flex max-w-xs items-center gap-1 rounded-md border py-1 pr-1 pl-3"
    >
      <button
        type="button"
        aria-label={text}
        title={text}
        onClick={onOpen}
        className="focus-visible:ring-ring min-w-0 flex-1 truncate rounded-sm text-left text-sm focus-visible:ring-3 focus-visible:outline-none"
      >
        {text}
      </button>
      {draft.createdTaskId === null ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={`Discard draft ${title}`}
          onClick={onDiscard}
        >
          <XIcon aria-hidden />
        </Button>
      ) : null}
    </div>
  );
}
